import React, { useState, useEffect } from "react";
import {collection, addDoc, getDocs, updateDoc, arrayUnion, doc} from "firebase/firestore";
import { useParams } from "react-router-dom";
import {db} from "../../firebase/firebase-config";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";

interface FormData {
    patientId: string;
    visitId: string;
    date: string;
    time: string;
    hasPain: string;
    painLevel: string;
    hasPharmacologicalIntervention: string;
    pharmacologicalIntervention: { time: string; action: string }[];
    nonPharmacologicalIntervention: string;
    syariahIntervention: string;
    tanggalKajiUlang: string;
    nurseName: string;
}

interface SyariahIntervention {
    id: string;
    range: string;
    intervention: string;
}

const InterventionVisitForm: React.FC<{ patientId?: string; visitId?: string }> = ({ patientId: propPatientId, visitId: propVisitId }) => {
    const { id: paramPatientId } = useParams<{ id: string }>();
    const patientId = propPatientId || paramPatientId || "";
    const visitId = propVisitId || ""; // Ambil dari prop, jika tidak ada gunakan string kosong
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setCurrentUser(user); // Simpan user yang login
                setFormData((prevData) => ({
                    ...prevData,
                    nurseName: user.displayName || "Unknown", // Simpan nama perawat
                }));
            }
        });
        return () => unsubscribe();
    }, []);

    const [formData, setFormData] = useState<FormData>({
        patientId: patientId,
        visitId: visitId,
        date: "",
        time: "",
        hasPain: "no",
        painLevel: "",
        hasPharmacologicalIntervention: "no",
        pharmacologicalIntervention: [],
        nonPharmacologicalIntervention: "Relaksasi Distraksi",
        syariahIntervention: "",
        tanggalKajiUlang: "",
        nurseName: currentUser?.displayName || "Unknown", // Tambahkan nama perawat

    });

    useEffect(() => {
        if (patientId) {
            setFormData((prevData) => ({ ...prevData, patientId }));
        }
    }, [patientId]);

    const [syariahInterventions, setSyariahInterventions] = useState<SyariahIntervention[]>([]);

    useEffect(() => {
        const fetchSyariahInterventions = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "syariah_interventions"));
                const data = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as SyariahIntervention[];
                setSyariahInterventions(data);
            } catch (error) {
                console.error("Error fetching Syariah Interventions:", error);
            }
        };

        fetchSyariahInterventions();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prevData) => {
            const updatedData = { ...prevData, [name]: value };

            if (name === "painLevel") {
                const painLevelNum = Number(value); // Pastikan ini angka
                if (!isNaN(painLevelNum)) {
                    const matchedIntervention = syariahInterventions.find((intervention) => {
                        const [min, max] = intervention.range.split("-").map(Number);
                        return painLevelNum >= min && painLevelNum <= max;
                    });
                    updatedData.syariahIntervention = matchedIntervention ? matchedIntervention.intervention : "";
                }
            }

            return updatedData;
        });
    };

    const handlePharmaChange = (index: number, field: "time" | "action", value: string) => {
        setFormData((prevData) => {
            const updatedPharma = [...prevData.pharmacologicalIntervention];
            updatedPharma[index] = { ...updatedPharma[index], [field]: value };
            return { ...prevData, pharmacologicalIntervention: updatedPharma };
        });
    };

    const addPharmaAction = () => {
        setFormData((prevData) => ({
            ...prevData,
            pharmacologicalIntervention: [...prevData.pharmacologicalIntervention, { time: "", action: "" }],
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.patientId) {
            alert("Patient ID is missing!");
            return;
        }

        try {
            // Tambahkan dokumen baru ke koleksi "intervention_records"
            const docRef = await addDoc(collection(db, "intervention_records"), {
                ...formData,
                visitId,
                nurseName: currentUser?.displayName || "Unknown", // Simpan nama perawat/bidan

            });

            // Update array interventionRecords di dokumen visit terkait
            const visitRef = doc(db, "visits", visitId);
            await updateDoc(visitRef, {
                interventionRecords: arrayUnion(docRef.id) // Menambahkan ID ke dalam array
            });

            alert("Data berhasil disimpan!");
        } catch (error) {
            console.error("Error saving data: ", error);
        }
    };

    return (
        <div className="py-4 px-5 bg-white dark:bg-gray-800 shadow-lg rounded-lg">
            <form onSubmit={handleSubmit} className="space-y-3">
                <input type="hidden" name="patientId" value={formData.patientId} />
                <div className="flex items-center gap-4">
                    <div className="w-1/2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date</label>
                        <input
                            type="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            required
                        />
                    </div>

                    <div className="w-1/2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Time</label>
                        <input
                            type="time"
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            required
                        />
                    </div>
                </div>

                {/* Pain Section */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Do you have pain?</label>
                    <div className="flex space-x-4 text-gray-900 dark:text-gray-200">
                        <label>
                            <input type="radio" name="hasPain" value="yes" checked={formData.hasPain === "yes"} onChange={handleChange} />
                            Yes
                        </label>
                        <label>
                            <input type="radio" name="hasPain" value="no" checked={formData.hasPain === "no"} onChange={handleChange} />
                            No
                        </label>
                    </div>
                </div>

                {/* Pain Level */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pain Level</label>
                    <input
                        type="number"
                        name="painLevel"
                        value={formData.painLevel}
                        onChange={handleChange}
                        className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        min="0"
                        max="10"
                        required
                    />
                </div>

                {/* Pharmacological Intervention */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pharmacological Intervention?</label>
                    <div className="flex space-x-4 text-gray-900 dark:text-gray-200">
                        <label>
                            <input type="radio" name="hasPharmacologicalIntervention" value="yes" checked={formData.hasPharmacologicalIntervention === "yes"} onChange={handleChange} />
                            Yes
                        </label>
                        <label>
                            <input type="radio" name="hasPharmacologicalIntervention" value="no" checked={formData.hasPharmacologicalIntervention === "no"} onChange={handleChange} />
                            No
                        </label>
                    </div>
                </div>

                {formData.hasPharmacologicalIntervention === "yes" && (
                    <div>
                        <table className="border-collapse border border-gray-700 dark:border-gray-500 w-full">
                            <thead>
                            <tr>
                                <th className="border border-gray-700 dark:border-gray-500 px-2 text-gray-900 dark:text-gray-200">Jam</th>
                                <th className="border border-gray-700 dark:border-gray-500 px-2 text-gray-900 dark:text-gray-200">Tindakan</th>
                            </tr>
                            </thead>
                            <tbody>
                            {formData.pharmacologicalIntervention.map((entry, index) => (
                                <tr key={index}>
                                    <td className="border border-gray-700 dark:border-gray-500">
                                        <input type="time" value={entry.time} onChange={(e) => handlePharmaChange(index, "time", e.target.value)} className="w-full p-2 bg-gray-50 dark:bg-gray-700 dark:text-white" required />
                                    </td>
                                    <td className="border border-gray-700 dark:border-gray-500">
                                        <input type="text" value={entry.action} onChange={(e) => handlePharmaChange(index, "action", e.target.value)} className="w-full p-2 bg-gray-50 dark:bg-gray-700 dark:text-white" required />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        {formData.pharmacologicalIntervention.length === 0 && (
                            <button type="button" onClick={addPharmaAction} className="mt-2 bg-blue-500 dark:bg-blue-700 text-white p-2 rounded hover:bg-blue-600 dark:hover:bg-blue-800">
                                Tambah Tindakan
                            </button>
                        )}
                    </div>
                )}

                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Syariah Intervention:</label>
                <textarea
                    name="syariahIntervention"
                    value={formData.syariahIntervention}
                    readOnly
                    className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-md bg-gray-100 dark:bg-gray-700 dark:text-white"
                />

                <div className="w-1/2 pb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reevaluation Time</label>
                    <input
                        type="time"
                        name="tanggalKajiUlang"
                        value={formData.tanggalKajiUlang}
                        onChange={handleChange}
                        className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                </div>
                <button type="submit" className="w-full bg-blue-600 dark:bg-blue-700 text-white p-2 rounded-md hover:bg-blue-700 dark:hover:bg-blue-800">
                    Submit
                </button>
            </form>
        </div>
    );
};
export default InterventionVisitForm;
